/**
 * Heading / back bearing info / editor. A single bearing line on one charted
 * object (or a tapped point): a HEADING bearing is to an object ahead and reads
 * as the course you steer to close it; a BACK bearing is to an object astern and
 * reads as the reciprocal you'd see looking back, confirming you're holding your
 * track. Toggle which it is, name it, file it under a plan, or delete it.
 * Read-only in Live, editable in Plotting, the same as clearings and transits.
 */
import { useEffect, useState } from 'react';
import { useShellStore } from '@/store/shell';
import { usePilotageStore } from '@/store/pilotage';
import {
  headingDisplayName,
  headingFromForLabelBearing,
  headingLabelBearingDeg,
  headingSteerDeg,
} from '@/pilotage';
import { formatBearing, formatPosition } from '@/nav';
import { Sheet } from './Sheet';

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="acp-kv">
      <span className="k">{k}</span>
      <span className="v">{v}</span>
    </div>
  );
}

export function HeadingSheet() {
  const activeId = usePilotageStore((s) => s.activeHeadingId);
  const headingBearings = usePilotageStore((s) => s.headingBearings);
  const plans = usePilotageStore((s) => s.plans);
  const updateHeadingBearing = usePilotageStore((s) => s.updateHeadingBearing);
  const removeHeadingBearing = usePilotageStore((s) => s.removeHeadingBearing);
  const setActiveHeadingBearing = usePilotageStore((s) => s.setActiveHeadingBearing);
  const assignHeadingToPlan = usePilotageStore((s) => s.assignHeadingToPlan);
  const addPlan = usePilotageStore((s) => s.addPlan);

  const mode = useShellStore((s) => s.mode);
  const closeSheet = useShellStore((s) => s.closeSheet);
  const showToast = useShellStore((s) => s.showToast);

  const hb = headingBearings.find((x) => x.id === activeId);

  const [name, setName] = useState('');
  const [liveBrg, setLiveBrg] = useState('');
  useEffect(() => {
    if (hb) {
      setName(hb.name ?? '');
      setLiveBrg(String(Math.round(headingLabelBearingDeg(hb))));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  if (!hb) return <Sheet name="heading" title="Bearing" />;

  const sighted = formatBearing(headingLabelBearingDeg(hb));
  const steer = formatBearing(headingSteerDeg(hb));
  const isBack = hb.kind === 'back';
  const readOnly = mode === 'Live';

  // The plain-English read of what to do with this bearing.
  const guide = isBack
    ? `Keep ${hb.anchorLabel || 'the mark'} astern on ${sighted}. That's the reciprocal of your course, so while it sits there you're holding ${steer}.`
    : `Steer ${steer} to close ${hb.anchorLabel || 'the mark'} ahead. Keep it on this bearing and you stay on the line.`;

  // Adjust the sighted bearing by typing it: we recompute the vessel-side end so
  // the object sits on the entered bearing, keeping the object-to-vessel
  // distance. Same as dragging the vessel end, just typed.
  const editBearing = (v: string) => {
    setLiveBrg(v);
    const n = parseFloat(v);
    if (hb && Number.isFinite(n)) {
      updateHeadingBearing(hb.id, { from: headingFromForLabelBearing(hb, n) });
    }
  };
  // Swap heading <-> back. The sighted bearing on the object stays the same; the
  // course it means flips to the reciprocal, so the arrow turns 180 and the line
  // reads the other way. This is the "swap to back bearing" action.
  const swapKind = () => {
    if (!hb) return;
    const toBack = hb.kind !== 'back';
    updateHeadingBearing(hb.id, { kind: toBack ? 'back' : 'heading' });
    showToast(toBack ? 'Swapped to back bearing' : 'Swapped to heading bearing');
  };

  if (readOnly) {
    return (
      <Sheet
        name="heading"
        title={isBack ? 'Back bearing' : 'Heading bearing'}
        sub="Edit the bearing, swap it front-to-back, or delete it. It still reads live while we're following."
      >
        <Row k="Name" v={headingDisplayName(hb)} />
        <Row k="On object" v={hb.anchorLabel || 'point'} />
        <Row k={isBack ? 'Bearing astern' : 'Steer'} v={isBack ? sighted : steer} />
        {isBack && <Row k="Your course" v={steer} />}
        <Row k="Object position" v={formatPosition(hb.anchor)} />
        <p className="sub" style={{ marginTop: 10 }}>
          {guide}
        </p>
        <label className="acp-fieldlabel">Bearing to the object (°T, what we sight)</label>
        <input
          className="acp-text-input"
          value={liveBrg}
          onChange={(e) => editBearing(e.target.value)}
          placeholder={String(Math.round(headingLabelBearingDeg(hb)))}
          inputMode="decimal"
          aria-label="Bearing to the object in degrees true"
        />
        <div className="acp-btn-row">
          <button type="button" className="acp-btn full" onClick={swapKind}>
            {isBack ? 'Swap to heading (ahead)' : 'Swap to back bearing (astern)'}
          </button>
        </div>
        {/* Deleting is removal, not editing a live guard, so we allow it in Live
            too, the same as a clearing bearing. */}
        <div className="acp-btn-row">
          <button
            type="button"
            className="acp-btn"
            onClick={() => {
              removeHeadingBearing(hb.id);
              setActiveHeadingBearing(null);
              closeSheet();
              showToast('Bearing deleted');
            }}
          >
            Delete
          </button>
          <button
            type="button"
            className="acp-btn primary"
            onClick={() => {
              setActiveHeadingBearing(null);
              closeSheet();
            }}
          >
            Done
          </button>
        </div>
      </Sheet>
    );
  }

  function del() {
    if (!hb) return;
    removeHeadingBearing(hb.id);
    setActiveHeadingBearing(null);
    closeSheet();
    showToast('Bearing deleted');
  }

  return (
    <Sheet
      name="heading"
      title={isBack ? 'Back bearing' : 'Heading bearing'}
      sub="A single bearing line on one object. Drag the vessel end on the chart to set the bearing; the label reads what you'd sight in °T."
    >
      <label className="acp-fieldlabel">Type</label>
      <div className="acp-seg">
        <button
          type="button"
          className={!isBack ? 'on' : ''}
          onClick={() => updateHeadingBearing(hb.id, { kind: 'heading' })}
        >
          Heading (ahead)
        </button>
        <button
          type="button"
          className={isBack ? 'on' : ''}
          onClick={() => updateHeadingBearing(hb.id, { kind: 'back' })}
        >
          Back (astern)
        </button>
      </div>

      <Row k={isBack ? 'Bearing astern' : 'Steer to object'} v={isBack ? sighted : steer} />
      {isBack && <Row k="Your course" v={steer} />}
      <Row k="On object" v={hb.anchorLabel || 'object / point'} />
      <p className="sub" style={{ marginTop: 6 }}>
        {guide}
      </p>

      <label className="acp-fieldlabel">Name</label>
      <input
        className="acp-text-input"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          updateHeadingBearing(hb.id, { name: e.target.value });
        }}
        placeholder={headingDisplayName(hb)}
      />
      <label className="acp-fieldlabel">Pilotage plan</label>
      <div className="acp-seg" style={{ flexWrap: 'wrap' }}>
        <button
          type="button"
          className={hb.planId === null ? 'on' : ''}
          onClick={() => assignHeadingToPlan(hb.id, null)}
        >
          Loose
        </button>
        {plans.map((p) => (
          <button
            key={p.id}
            type="button"
            className={hb.planId === p.id ? 'on' : ''}
            onClick={() => assignHeadingToPlan(hb.id, p.id)}
          >
            {p.name}
          </button>
        ))}
        <button
          type="button"
          onClick={() => {
            const id = addPlan();
            assignHeadingToPlan(hb.id, id);
            showToast('Added to a new plan');
          }}
        >
          + New plan
        </button>
      </div>
      <div className="acp-btn-row">
        <button type="button" className="acp-btn" onClick={del}>
          Delete
        </button>
        <button
          type="button"
          className="acp-btn primary"
          onClick={() => {
            setActiveHeadingBearing(null);
            closeSheet();
          }}
        >
          Done
        </button>
      </div>
    </Sheet>
  );
}
